import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { PublicLayout } from '../../components/public/PublicLayout'
import { MultiStepForm } from '../../components/public/MultiStepForm'
import {
  getInfoEncuestaPublica,
  getFormularioEncuestaPublica,
  enviarEncuestaPublica,
} from '../../api/publicoApi'
import type { PasoPublico } from '../../types'

interface EncuestaInfo {
  uuid: string
  titulo: string
  descripcion: string
  imagen_url: string | null
  mensaje_bienvenida: string
  tiempo_estimado: number
}

interface EncuestaFormulario {
  campania_titulo: string
  total_pasos: number
  pasos: PasoPublico[]
}

export function EncuestaPublicaPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()

  useDocumentTitle('Encuesta')

  const [info, setInfo] = useState<EncuestaInfo | null>(null)
  const [formulario, setFormulario] = useState<EncuestaFormulario | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!uuid) return
    let cancelled = false
    const load = async () => {
      try {
        const infoData = await getInfoEncuestaPublica(uuid)
        if (cancelled) return
        setInfo(infoData)
        const formData = await getFormularioEncuestaPublica(uuid)
        if (cancelled) return
        setFormulario(formData)
      } catch (err: unknown) {
        if (cancelled) return
        const status = (err as { response?: { status?: number; data?: { error?: string } } })?.response?.status
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        if (status === 410) {
          navigate(`/muestras-medicas/no-disponible?mensaje=${encodeURIComponent(msg || 'Encuesta no disponible')}`, { replace: true })
          return
        }
        setError(msg || 'No se pudo cargar la encuesta')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [uuid, navigate])

  if (loading) {
    return (
      <PublicLayout>
        <div className="space-y-4">
          <Skeleton className="h-12 w-64 mx-auto" />
          <Skeleton className="h-96 w-full" />
        </div>
      </PublicLayout>
    )
  }

  if (error || !info || !formulario) {
    return (
      <PublicLayout>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">{error || 'Encuesta no disponible'}</p>
          </CardContent>
        </Card>
      </PublicLayout>
    )
  }

  return (
    <PublicLayout>
      <MultiStepForm
        pasos={formulario.pasos}
        mensajeBienvenida={info.mensaje_bienvenida}
        tiempoEstimado={info.tiempo_estimado}
        imagenUrl={info.imagen_url}
        titulo={info.titulo}
        onSubmit={async (respuestas) => {
          await enviarEncuestaPublica(uuid!, {
            paso_numero: formulario.total_pasos,
            respuestas,
          })
          navigate(`/muestras-medicas/gracias/${uuid}?tipo=encuesta`, { replace: true })
        }}
      />
    </PublicLayout>
  )
}
